import * as Sentry from '@sentry/react';

export type ApiErrorClassification = 'network' | 'auth' | 'permission' | 'validation' | 'rate_limit' | 'server' | 'client' | 'unknown';

export type ApiErrorContext = {
  connected: boolean;
  hasFailAction: boolean;
  isTokenRefresh?: boolean;
  method: string;
  url: string;
};

export type ApiErrorMetadata = {
  classification: ApiErrorClassification;
  connected: boolean;
  handledByUi: boolean;
  method: string;
  route: string;
  shouldReport: boolean;
  status: number;
};

const VALIDATION_STATUSES = new Set([400, 409, 412, 422]);
const TRANSPORT_ERROR_CODES = new Set(['ECONNABORTED', 'ERR_NETWORK']);

export class ApiError extends Error {
  data: any;
  originalError: any;
  response: any;
  classification: ApiErrorClassification;
  connected: boolean;
  handledByUi: boolean;
  method: string;
  route: string;
  shouldReport: boolean;
  status: number;

  constructor(originalError: any, data: any, metadata: ApiErrorMetadata) {
    super(originalError?.message || data?.message || 'API request failed');
    this.name = 'ApiError';
    // Restore the subclass prototype so runtime checks like `instanceof ApiError`
    // keep working after extending the built-in Error type.
    Object.setPrototypeOf(this, new.target.prototype);
    this.data = data;
    this.originalError = originalError;
    this.response = originalError?.response;
    this.classification = metadata.classification;
    this.connected = metadata.connected;
    this.handledByUi = metadata.handledByUi;
    this.method = metadata.method;
    this.route = metadata.route;
    this.shouldReport = metadata.shouldReport;
    this.status = metadata.status;
  }
}

/** Type guard for helpers that only operate on ApiError instances. */
export const isApiError = (error: unknown): error is ApiError => {
  return error instanceof ApiError;
};

/** Removes query params so Sentry grouping stays stable across equivalent routes. */
const normalizeApiRoute = (url: string): string => {
  return (url || 'unknown').split('?')[0];
};

/** Treat auth endpoints as expected session-management flows, not generic API routes. */
const isAuthRoute = (route: string, isTokenRefresh: boolean = false): boolean => {
  if (isTokenRefresh) {
    return true;
  }
  return route.startsWith('/api/oauth/') || route.startsWith('v1/oauth/');
};

/** Falls back to 503 when transport failures never received an HTTP response. */
const getStatusFromError = (error: any): number => {
  return error?.response?.status || error?.status || 503;
};

/** Detects browser/network transport failures that happen before an HTTP status exists. */
const isTransportError = (error: any): boolean => {
  const message = String(error?.message || '').toLowerCase();
  return !error?.response && (TRANSPORT_ERROR_CODES.has(error?.code) || message.includes('network error') || message.includes('timeout'));
};

/** Maps raw request failures into broad, stable buckets for reporting policy and triage. */
const classifyApiError = (error: any, context: ApiErrorContext, route: string, status: number): ApiErrorClassification => {
  if (!context.connected || isTransportError(error)) {
    return 'network';
  }

  if (status === 401) {
    return 'auth';
  }

  if (status === 403) {
    return isAuthRoute(route, context.isTokenRefresh) ? 'auth' : 'permission';
  }

  if (status === 429) {
    return 'rate_limit';
  }

  if (status >= 500) {
    return 'server';
  }

  if (VALIDATION_STATUSES.has(status)) {
    return isAuthRoute(route, context.isTokenRefresh) ? 'auth' : 'validation';
  }

  if (status >= 400) {
    return 'client';
  }

  return 'unknown';
};

/** Decides whether the classified error should create a Sentry issue or stay contextual only. */
const shouldReportApiError = (classification: ApiErrorClassification, context: ApiErrorContext, route: string): boolean => {
  if (classification === 'auth' && isAuthRoute(route, context.isTokenRefresh)) {
    return false;
  }

  if ((classification === 'validation' || classification === 'permission') && context.hasFailAction) {
    return false;
  }

  return true;
};

/** Builds the normalized metadata attached to ApiError and used by Sentry filtering. */
export const buildApiErrorMetadata = (error: any, context: ApiErrorContext): ApiErrorMetadata => {
  const status = getStatusFromError(error);
  const route = normalizeApiRoute(context.url);
  const classification = classifyApiError(error, context, route, status);
  const handledByUi = Boolean(context.hasFailAction);

  return {
    classification,
    connected: context.connected,
    handledByUi,
    method: context.method.toUpperCase(),
    route,
    shouldReport: shouldReportApiError(classification, context, route),
    status
  };
};

/** Leaves a short trail in Sentry so failures still have request context even when dropped later. */
export const addApiErrorBreadcrumb = (apiError: ApiError): void => {
  Sentry.addBreadcrumb({
    category: 'api',
    message: `${apiError.method} ${apiError.route} failed`,
    level: apiError.shouldReport ? 'error' : 'warning',
    data: {
      classification: apiError.classification,
      connected: apiError.connected,
      handledByUi: apiError.handledByUi,
      status: apiError.status
    }
  });
};

/**
 * Sentry `beforeSend` hook for ApiError instances.
 * `hint` is the extra callback context Sentry provides alongside the event.
 * We use `hint.originalException` to inspect the real thrown error object before
 * it is normalized into a Sentry event, so we can either drop expected errors
 * or enrich reported ones with stable tags and grouping data.
 */
export const filterSentryApiErrorEvent = (event: any, hint: any) => {
  const originalException = hint?.originalException;
  if (!isApiError(originalException)) {
    return event;
  }

  if (!originalException.shouldReport) {
    return null;
  }

  event.tags = {
    ...event.tags,
    api_classification: originalException.classification,
    api_method: originalException.method,
    api_route: originalException.route,
    api_status: String(originalException.status),
    handled_by_ui: String(originalException.handledByUi)
  };
  event.extra = {
    ...event.extra,
    api_error: {
      classification: originalException.classification,
      connected: originalException.connected,
      route: originalException.route,
      status: originalException.status,
      userMessage: originalException.data?.message
    }
  };
  event.fingerprint = ['api', originalException.classification, originalException.method, originalException.route, String(originalException.status)];
  return event;
};
